import * as Yup from "yup";

interface Payment {
  amount: string;
  isDeleted?: boolean;
}

export const validationSchema = Yup.object().shape({
  vehicleNumber: Yup.string().required("Vehicle number is required"),
  expenses: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required("Expense type is required"),
      paidTo: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) => schema.required("Petrol Pump is required"),
        otherwise: (schema) =>
          schema.when("type", {
            is: (type: string) =>
              ["Maintenance / Repair", "Minor Repair"].includes(type),
            then: (schema) => schema.required("Service provider is required"),
            otherwise: (schema) =>
              schema.when("type", {
                is: "Driver/Cleaner",
                then: (schema) => schema.required("Staff member is required"),
                otherwise: (schema) =>
                  schema.when("type", {
                    is: "Parking",
                    then: (schema) =>
                      schema.required("Parking location is required"),
                    otherwise: (schema) =>
                      schema.when("type", {
                        is: "Challan",
                        then: (schema) =>
                          schema.required("Authority/Department is required"),
                        otherwise: (schema) =>
                          schema.when("type", {
                            is: "Outsourced Owner",
                            then: (schema) =>
                              schema.required("Owner name is required"),
                            otherwise: (schema) =>
                              schema.when("type", {
                                is: "Pickup Man",
                                then: (schema) =>
                                  schema.required(
                                    "Pickup man name is required"
                                  ),
                                otherwise: (schema) =>
                                  schema.when("type", {
                                    is: "Other",
                                    then: (schema) =>
                                      schema.required(
                                        "Expense type is required"
                                      ),
                                    otherwise: (schema) =>
                                      schema.required("Paid to is required"),
                                  }),
                              }),
                          }),
                      }),
                  }),
              }),
          }),
      }),
      amount: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) => schema.required("Fule Expense amount is required"),
        otherwise: (schema) =>
          schema.when("type", {
            is: "Maintenance / Repair",
            then: (schema) =>
              schema.required("Maintenance/Repair amount is required"),
            otherwise: (schema) =>
              schema.when("type", {
                is: "Driver/Cleaner",
                then: (schema) =>
                  schema.required("Driver/Cleaner amount is required"),
                otherwise: (schema) =>
                  schema.when("type", {
                    is: "Parking",
                    then: (schema) =>
                      schema.required("Parking amount is required"),
                    otherwise: (schema) =>
                      schema.when("type", {
                        is: "Challan",
                        then: (schema) =>
                          schema.required("Challan amount is required"),
                        otherwise: (schema) =>
                          schema.when("type", {
                            is: "Outsourced Owner",
                            then: (schema) =>
                              schema.required(
                                "Outsourced Owner amount is required"
                              ),
                            otherwise: (schema) =>
                              schema.when("type", {
                                is: "Pickup Man",
                                then: (schema) =>
                                  schema.required(
                                    "Pickup man amount is required"
                                  ),
                                otherwise: (schema) =>
                                  schema.when("type", {
                                    is: "Other",
                                    then: (schema) =>
                                      schema.required(
                                        "Other Expense amount is required"
                                      ),
                                    otherwise: (schema) =>
                                      schema.when("type", {
                                        is: "Minor Repair",
                                        then: (schema) =>
                                          schema.required(
                                            "Repair item amount is required"
                                          ),
                                        otherwise: (schema) =>
                                          schema.required(
                                            "Expense amount is required"
                                          ),
                                      }),
                                  }),
                              }),
                          }),
                      }),
                  }),
              }),
          }),
      }),
      location: Yup.string().when("type", {
        is: "Parking",
        then: (schema) => schema.required("Location is required"),
        otherwise: (schema) =>
          schema
            .optional()
            .max(100, "Location must not exceed 100 characters")
            .nullable(),
      }),
      notes: Yup.string().max(500, "Notes must not exceed 500 characters"),
      contactNumber: Yup.string()
        .matches(/^[0-9]*$/, "Contact number must contain only digits")
        .test(
          "min-length",
          "Contact number must be at least 10 digits",
          (value) => !value || value.length >= 10
        )
        .max(15, "Contact number must not exceed 15 digits")
        .nullable(),
      fuelType: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) => schema.required("Fuel type is required"),
      }),
      liters: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) =>
          schema
            .required("Fuel quantity is required")
            .matches(
              /^[0-9]*\.?[0-9]+$/,
              "Liters must be a valid decimal number"
            ),
      }),
      petrolPumpId: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) => schema.optional(), // Optional since manual entry is allowed
      }),
      fuelCardId: Yup.string()
        .nullable()
        .when(["type", "isFuelCard"], {
          is: (type: string, isFuelCard: boolean) =>
            type === "Fuel" && isFuelCard,
          then: (schema) =>
            schema
              .required("Fuel card is required")
              .min(1, "Please select a fuel card"),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      odometerReading: Yup.string().optional().nullable(),
      pricePerLitre: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) =>
          schema
            .required("Price per litre is required")
            .matches(
              /^[0-9]*\.?[0-9]+$/,
              "Price per litre must be a valid decimal number"
            ),
      }),
      bulkDiscountApplied: Yup.string().when("type", {
        is: "Fuel",
        then: (schema) =>
          schema.matches(
            /^$|^[0-9]*\.?[0-9]+$/,
            "Bulk discount must be a valid decimal number"
          ),
      }),
      serviceType: Yup.string().when("type", {
        is: (type: string) => ["Maintenance / Repair"].includes(type),
        then: (schema) => schema.required("Service type is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      serviceProvider: Yup.string().when("type", {
        is: (type: string) => ["Maintenance / Repair"].includes(type),
        then: (schema) => schema.required("Service provider is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      // Maintenance/Repair specific validations
      maintenanceItem: Yup.string().when("type", {
        is: "Maintenance / Repair",
        then: (schema) => schema.required("Maintenance item is required"),
        otherwise: (schema) =>
          schema.when("type", {
            is: "Minor Repair",
            then: (schema) => schema.required("Repair item is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),
      }),
      customMaintenanceItem: Yup.string().when(["type", "maintenanceItem"], {
        is: (type: string, maintenanceItem: string) =>
          ["Maintenance / Repair"].includes(type) &&
          maintenanceItem === "Custom",
        then: (schema) =>
          schema.required("Custom maintenance item is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      actionType: Yup.string().when("type", {
        is: (type: string) => ["Maintenance / Repair"].includes(type),
        then: (schema) => schema.required("Action type is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      warrantyStartDate: Yup.string().when(["type", "actionType"], {
        is: (type: string, actionType: string) =>
          ["Maintenance / Repair"].includes(type) && actionType === "Replaced",
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      warrantyPeriod: Yup.string().when(["type", "actionType"], {
        is: (type: string, actionType: string) =>
          ["Maintenance / Repair"].includes(type) && actionType === "Replaced",
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      customWarrantyPeriod: Yup.string().when(
        ["type", "actionType", "warrantyPeriod"],
        {
          is: (type: string, actionType: string, warrantyPeriod: string) =>
            ["Maintenance / Repair"].includes(type) &&
            actionType === "Replaced" &&
            warrantyPeriod === "Custom",
          then: (schema) =>
            schema.required("Custom warranty period is required"),
          otherwise: (schema) => schema.optional().nullable(),
        }
      ),

      nextServiceDue: Yup.string().when("type", {
        is: (type: string) => ["Maintenance / Repair"].includes(type),
        then: (schema) =>
          schema
            .optional()
            .matches(/^[0-9]*$/, "Next service due must be a valid number"),
      }),
      // Category validations for different expense types
      category: Yup.string().when("type", {
        is: "Driver/Cleaner",
        then: (schema) =>
          schema
            .required("Staff type is required")
            .oneOf(["DRIVER", "CLEANER"], "Invalid staff type"),
        otherwise: (schema) =>
          schema.when("type", {
            is: "Other",
            then: (schema) => schema.required("Other category is required"),
            otherwise: (schema) =>
              schema.when("type", {
                is: "Insurance",
                then: (schema) =>
                  schema.required("Insurance category is required"),
                otherwise: (schema) =>
                  schema.when("type", {
                    is: "Parking",
                    then: (schema) =>
                      schema.required("Parking category is required"),
                    otherwise: (schema) => schema.optional(),
                  }),
              }),
          }),
      }),
      customCategory: Yup.string().when(["type", "category"], {
        is: (type: string, category: string) =>
          type === "Other" && category === "Custom",
        then: (schema) =>
          schema.required("Custom other category name is required"),
        otherwise: (schema) =>
          schema.when(["type", "category"], {
            is: (type: string, category: string) =>
              type === "Insurance" && category === "Custom",
            then: (schema) =>
              schema.required("Custom insurance category name is required"),
            otherwise: (schema) => schema.optional(),
          }),
      }),
      referenceId: Yup.string().when(["type", "category"], {
        is: (type: string, category: string) =>
          (["Other", "Insurance"].includes(type) &&
            ["DRIVER", "CLEANER", "PARKING"].includes(category)) ||
          (type === "Driver/Cleaner" &&
            ["DRIVER", "CLEANER"].includes(category)) ||
          type === "Parking",
        then: (schema) => schema.optional(), // Optional since manual entry is allowed
      }),
      responsiblePartyContact: Yup.string()
        .optional()
        .matches(/^[0-9]*$/, "Contact number must contain only digits")
        .test(
          "min-length",
          "Contact number must be at least 10 digits",
          (value) => !value || value.length >= 10
        )
        .max(15, "Contact number must not exceed 15 digits"),
      expenseDate: Yup.string().required("Date is required"),
      challanType: Yup.string().when("type", {
        is: "Challan",
        then: (schema) => schema.required("Challan type is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      challanAction: Yup.string().when("type", {
        is: "Challan",
        then: (schema) => schema.required("Challan action taken is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      responsibleParty: Yup.string().when("type", {
        is: "Challan",
        then: (schema) =>
          schema.required("Challan responsible party is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      responsiblePartyId: Yup.string().optional().nullable(),
      payments: Yup.array()
        .of(
          Yup.object().shape({
            type: Yup.string()
              .required("Payment type is required")
              .oneOf(
                ["ADVANCE", "SETTLEMENT", "FINAL"],
                "Invalid payment type"
              ),
            mode: Yup.string()
              .required("Payment mode is required")
              .oneOf(
                [
                  "CASH",
                  "BANK_TRANSFER",
                  "UPI",
                  "CHEQUE",
                  "ONLINE",
                  "OTHER",
                  "FUEL_CARD",
                  "ADVANCE_CASH",
                ],
                "Invalid payment mode"
              ),
            amount: Yup.string()
              .required("Payment amount is required")
              .matches(
                /^[0-9]*\.?[0-9]+$/,
                "Amount must be a valid decimal number"
              ),
            date: Yup.string().required("Payment date is required"),
            location: Yup.string().nullable(),
            receivedFrom: Yup.string().nullable(),
            remarks: Yup.string().nullable(),
            isDeleted: Yup.boolean(),
            id: Yup.string(),
            isEdited: Yup.boolean(),
            status: Yup.string().nullable(),
          })
        )
        .test(
          "total-not-exceeding",
          "Total payments cannot exceed expense amount",
          function (payments: Payment[] = []) {
            const expense = this.parent;
            const totalPaid = payments
              .filter((p) => !p.isDeleted)
              .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            return totalPaid <= Number(expense.amount);
          }
        ),
    })
  ),
  payments: Yup.array().of(
    Yup.object().shape({
      type: Yup.string()
        .required("Payment type is required")
        .oneOf(["ADVANCE", "SETTLEMENT", "FINAL"], "Invalid payment type"),
      mode: Yup.string()
        .required("Payment mode is required")
        .oneOf(
          [
            "CASH",
            "BANK_TRANSFER",
            "UPI",
            "CHEQUE",
            "ONLINE",
            "OTHER",
            "FUEL_CARD",
            "ADVANCE_CASH",
          ],
          "Invalid payment mode"
        ),
      amount: Yup.string()
        .required("Payment amount is required")
        .matches(/^[0-9]*\.?[0-9]+$/, "Amount must be a valid decimal number"),
    })
  ),
});
